import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { getSugerenciaCompra, getProveedores } from '../../lib/stockApi'

export default function OrdenCompra() {
  const [proveedorId, setProveedorId] = useState('')
  const [dias, setDias] = useState(7)
  const [proveedores, setProveedores] = useState([])
  const [items, setItems] = useState([])  // [{producto_id, sku, ..., cantidad: editable, marcado: bool}]
  const [loading, setLoading] = useState(false)

  useEffect(() => { getProveedores().then(setProveedores) }, [])
  useEffect(() => { load() }, [proveedorId, dias])

  async function load() {
    setLoading(true)
    const data = await getSugerenciaCompra(proveedorId || null, dias)
    setItems(data.map(d => ({
      ...d,
      cantidad: Number(d.comprar),
      marcado: true,  // por defecto todos marcados
    })))
    setLoading(false)
  }

  function toggle(producto_id) {
    setItems(items.map(it => it.producto_id === producto_id ? { ...it, marcado: !it.marcado } : it))
  }

  function setCantidad(producto_id, v) {
    setItems(items.map(it => it.producto_id === producto_id ? { ...it, cantidad: Math.max(0, v) } : it))
  }

  function toggleAll() {
    const algunoNoMarcado = items.some(it => !it.marcado)
    setItems(items.map(it => ({ ...it, marcado: algunoNoMarcado })))
  }

  function imprimir() {
    const seleccionados = items.filter(it => it.marcado && it.cantidad > 0)
    if (seleccionados.length === 0) {
      alert('Marcá al menos un producto con cantidad > 0')
      return
    }
    // Abre nueva pestaña con HTML print-friendly
    const w = window.open('', '_blank')
    const provName = proveedorId
      ? proveedores.find(p => p.id === proveedorId)?.nombre
      : 'Todos los proveedores'
    const total = seleccionados.reduce((s, it) => s + it.cantidad * Number(it.costo_unitario), 0)
    w.document.write(printHTML({ provName, items: seleccionados, total, dias }))
    w.document.close()
    setTimeout(() => w.print(), 500)
  }

  const totalSeleccionado = useMemo(() => {
    return items.filter(it => it.marcado).reduce((s, it) => s + it.cantidad * Number(it.costo_unitario), 0)
  }, [items])

  const cantSeleccionados = items.filter(it => it.marcado && it.cantidad > 0).length

  // Agrupado por proveedor para vista "Todos"
  const itemsAgrupados = useMemo(() => {
    if (proveedorId) return [{ proveedor: null, items }]
    const groups = {}
    items.forEach(it => {
      const key = it.proveedor || '— sin proveedor —'
      if (!groups[key]) groups[key] = []
      groups[key].push(it)
    })
    return Object.entries(groups).map(([proveedor, items]) => ({ proveedor, items }))
  }, [items, proveedorId])

  return (
    <AdminLayout>
      <div className="page">
        <h1>Orden de compra</h1>
        <p className="lead">Lista sugerida según stock + entregas próximas. Marcá lo que vas a comprar e imprimí.</p>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>Proveedor</label>
            <select value={proveedorId} onChange={e => setProveedorId(e.target.value)}>
              <option value="">Todos los proveedores</option>
              {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>Cubrir</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {[7, 14, 30].map(d => (
                <button key={d} className={`btn ${dias === d ? 'primary' : 'ghost'}`} style={{ padding: '8px 14px', fontSize: 13 }} onClick={() => setDias(d)}>{d}d</button>
              ))}
            </div>
          </div>
          <button className="btn ghost" onClick={load}>↺</button>
        </div>

        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Productos sugeridos</h3>
            <div className="stat">{items.length}</div>
          </div>
          <div className="card">
            <h3>Marcados</h3>
            <div className="stat" style={{ color: 'var(--teal)' }}>{cantSeleccionados}</div>
          </div>
          <div className="card" style={{ borderLeft: '4px solid var(--navy)' }}>
            <h3>Total estimado</h3>
            <div className="stat">${totalSeleccionado.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</div>
            <div className="desc">Sin IVA</div>
          </div>
        </div>

        <div className="btn-row">
          <button className="btn primary" onClick={imprimir} disabled={cantSeleccionados === 0}>
            🖨️ Imprimir orden ({cantSeleccionados})
          </button>
          <button className="btn ghost" onClick={toggleAll}>
            {items.some(it => !it.marcado) ? 'Marcar todos' : 'Desmarcar todos'}
          </button>
        </div>

        {loading ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>Cargando...</div>
        ) : items.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>
            🎉 No hay nada que comprar — el stock cubre las próximas entregas.
          </div>
        ) : itemsAgrupados.map(grp => (
          <div key={grp.proveedor || 'g'} style={{ marginBottom: 24 }}>
            {grp.proveedor && (
              <h3 style={{ fontSize: 13, fontWeight: 800, color: 'var(--navy-ink)', letterSpacing: '.1em', textTransform: 'uppercase', margin: '20px 0 8px', padding: '8px 12px', background: 'var(--paper)', borderRadius: 6 }}>
                ▸ {grp.proveedor} · {grp.items.length} productos
              </h3>
            )}
            <table className="t-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>SKU</th>
                  <th>Producto</th>
                  <th>Pres.</th>
                  <th style={{ textAlign: 'right' }}>Stock</th>
                  <th style={{ textAlign: 'right' }}>Necesario</th>
                  <th style={{ textAlign: 'right' }}>Sugerido</th>
                  <th style={{ textAlign: 'right' }}>Cantidad</th>
                  <th style={{ textAlign: 'right' }}>Costo u.</th>
                  <th style={{ textAlign: 'right' }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {grp.items.map(it => (
                  <tr key={it.producto_id} style={{ opacity: it.marcado ? 1 : 0.4 }}>
                    <td><input type="checkbox" checked={it.marcado} onChange={() => toggle(it.producto_id)} /></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--muted)' }}>{it.sku}</td>
                    <td><strong>{it.nombre}</strong></td>
                    <td style={{ fontSize: 13 }}>{it.presentacion}</td>
                    <td className="num" style={{ textAlign: 'right' }}>{it.stock_actual}</td>
                    <td className="num" style={{ textAlign: 'right', color: 'var(--muted)' }}>{Number(it.necesario_dias).toFixed(0)}</td>
                    <td className="num" style={{ textAlign: 'right', color: 'var(--teal)', fontWeight: 700 }}>{Number(it.comprar).toFixed(0)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number" min="0" step="1"
                        value={it.cantidad}
                        onChange={e => setCantidad(it.producto_id, parseInt(e.target.value) || 0)}
                        style={{ width: 70, textAlign: 'right' }}
                        disabled={!it.marcado}
                      />
                    </td>
                    <td className="num" style={{ textAlign: 'right', fontSize: 13 }}>${Number(it.costo_unitario).toFixed(2)}</td>
                    <td className="num" style={{ textAlign: 'right', fontWeight: 700 }}>
                      ${(it.cantidad * Number(it.costo_unitario)).toLocaleString('es-UY', { maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </AdminLayout>
  )
}

function printHTML({ provName, items, total, dias }) {
  const today = new Date().toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' })
  // Agrupar por proveedor para impresión
  const groups = {}
  items.forEach(it => {
    const k = it.proveedor || '— sin proveedor —'
    if (!groups[k]) groups[k] = []
    groups[k].push(it)
  })

  const groupsHTML = Object.entries(groups).map(([prov, its]) => {
    const subtotal = its.reduce((s, it) => s + it.cantidad * Number(it.costo_unitario), 0)
    const rows = its.map(it => `
      <tr>
        <td style="padding:8px 6px;border-bottom:1px solid #ddd"><input type="checkbox" /></td>
        <td style="padding:8px 6px;border-bottom:1px solid #ddd;font-family:monospace;font-size:11px;color:#666">${it.sku}</td>
        <td style="padding:8px 6px;border-bottom:1px solid #ddd"><strong>${escapeHtml(it.nombre)}</strong> · ${escapeHtml(it.presentacion || '')}</td>
        <td style="padding:8px 6px;border-bottom:1px solid #ddd;text-align:right;font-weight:700;font-size:16px">${it.cantidad}</td>
        <td style="padding:8px 6px;border-bottom:1px solid #ddd;text-align:right">$${Number(it.costo_unitario).toFixed(2)}</td>
        <td style="padding:8px 6px;border-bottom:1px solid #ddd;text-align:right">$${(it.cantidad * Number(it.costo_unitario)).toLocaleString('es-UY', { maximumFractionDigits: 0 })}</td>
      </tr>
    `).join('')
    return `
      <h2 style="margin-top:20px;background:#1A3461;color:#fff;padding:8px 12px;font-size:14px;letter-spacing:.08em;text-transform:uppercase">${escapeHtml(prov)} — ${its.length} productos · subtotal $${subtotal.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</h2>
      <table style="width:100%;border-collapse:collapse;font-size:13px">
        <thead><tr style="background:#F1F5F9">
          <th style="padding:8px 6px;text-align:left;border-bottom:2px solid #1A3461;width:24px">✓</th>
          <th style="padding:8px 6px;text-align:left;border-bottom:2px solid #1A3461">SKU</th>
          <th style="padding:8px 6px;text-align:left;border-bottom:2px solid #1A3461">Producto</th>
          <th style="padding:8px 6px;text-align:right;border-bottom:2px solid #1A3461">Cant.</th>
          <th style="padding:8px 6px;text-align:right;border-bottom:2px solid #1A3461">Costo u.</th>
          <th style="padding:8px 6px;text-align:right;border-bottom:2px solid #1A3461">Subtotal</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    `
  }).join('')

  return `
    <!DOCTYPE html>
    <html><head><meta charset="UTF-8">
    <title>Orden de compra Limpack</title>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap" rel="stylesheet" />
    <style>
      @page { size: A4; margin: 15mm }
      body { font-family: 'Inter', sans-serif; color: #0F172A; padding: 0; margin: 0 }
      .header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #1A3461; padding-bottom: 12px; margin-bottom: 16px }
      .brand { font-size: 28px; font-weight: 900; color: #0F1F3D; letter-spacing: -.02em }
      .brand em { font-style: normal; color: #00A8B5 }
      .meta { text-align: right; font-size: 12px; color: #666 }
      .meta b { display: block; color: #0F1F3D; font-size: 13px; margin-bottom: 2px }
      h1 { font-size: 22px; margin: 0 0 16px; color: #0F1F3D }
      .total-box { margin-top: 20px; padding: 14px; background: #1A3461; color: #fff; text-align: right; font-size: 18px; font-weight: 800 }
      .footer { margin-top: 24px; font-size: 10px; color: #999; text-align: center }
      @media print { .no-print { display: none } }
    </style>
    </head><body>
      <div class="header">
        <div>
          <div class="brand">Lim<em>pack</em></div>
          <div style="font-size:11px;color:#666;margin-top:2px">Limpieza, resuelta cada mes.</div>
        </div>
        <div class="meta">
          <b>Orden de compra</b>
          ${today} · cobertura ${dias} días<br/>
          ${provName ? `Proveedor: <strong>${escapeHtml(provName)}</strong>` : 'Múltiples proveedores'}
        </div>
      </div>
      <h1>Lista de compra</h1>
      ${groupsHTML}
      <div class="total-box">TOTAL: $${total.toLocaleString('es-UY', { maximumFractionDigits: 0 })} <span style="font-size:11px;font-weight:600;opacity:.7">sin IVA</span></div>
      <div class="footer">Limpack · limpack.uy · hola@limpack.uy · WhatsApp 098 610 001</div>
    </body></html>
  `
}

function escapeHtml(s) {
  if (!s) return ''
  return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]))
}
